import { ReactNode, useState } from 'react';

import moment from 'moment';
import { Link, useParams } from 'react-router-dom';

import { Permission } from '../../../../../../../backend/src/auth/permissions/permission';
import { RunType } from '../../../../../../../backend/src/run/run-type';
import { RunStatus } from '../../../../../../../backend/src/types/types';
import { blockedReasonMessage } from '../../../../../../../backend/src/utils/billing-utils';
import { useUserContext } from '../../../../../Contexts/User';
import { TrackingEventsV2 } from '@/analytics/eventsV2';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { useSubmitRunApproval } from '@/api/mutations/useSubmitRunApproval';
import formatDate from '@/helpers/formatDate';
import { useHasPermission } from '@/hooks/useHasPermission';
import type { RunWithSubsetName } from '@/new/components/breadcrumb';
import { Icon } from '@/new/components/icon';
import { formatDurationEstimate } from '@/new/components/processing-status-badge';
import { RunApprovalPopoverContent } from '@/new/components/run-approval-popover-content';
import { Button } from '@/new/components/shadcn/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/new/components/shadcn/popover';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/new/components/shadcn/tooltip';
import { isProcessingStatus, PipelineProcessingStageDescriptions, ProcessingStatus } from '@/new/lib/pipeline-status';
import { cn } from '@/new/lib/utils';

type StatusVariant = 'amber' | 'red' | 'sky' | 'gray';

const variantStyles: Record<StatusVariant, { container: string; title: string; sub: string }> = {
  amber: {
    container: 'border-amber-200 bg-amber-50/60',
    title: 'text-amber-700',
    sub: 'text-amber-700',
  },
  red: {
    container: 'border-red-200 bg-red-50/50',
    title: 'text-red-900',
    sub: 'text-red-800',
  },
  sky: {
    container: 'border-sky-300 bg-sky-50/60',
    title: 'text-sky-600',
    sub: 'text-sky-600',
  },
  gray: {
    container: 'border-gray-200 bg-gray-50',
    title: 'text-gray-900',
    sub: 'text-gray-600',
  },
};

interface StatusCardProps {
  variant: StatusVariant;
  /** Full icon className, including its colour (e.g. 'lucide--circle-check text-green-500'). */
  iconClassName: string;
  title: string;
  subText: ReactNode;
  /** Optional action rendered after the sub text, separated by a dot. */
  action?: ReactNode;
  /**
   * Where the action sits relative to the sub text:
   * - 'inline' (default): same row, separated by a dot.
   * - 'block': on its own line below the sub text.
   */
  actionPlacement?: 'inline' | 'block';
}

/**
 * Compact status card laid out for constrained-width panels:
 *
 *   icon   title text
 *          sub text · action      (actionPlacement='inline')
 *
 *   icon   title text
 *          sub text
 *          action                 (actionPlacement='block')
 */
export function StatusCard({
  variant,
  iconClassName,
  title,
  subText,
  action,
  actionPlacement = 'inline',
}: StatusCardProps) {
  const styles = variantStyles[variant];

  const isBlock = actionPlacement === 'block';

  return (
    <div
      className={cn(
        'flex min-w-0 gap-3 rounded-lg border p-2.75 px-3.75',
        isBlock ? 'items-center justify-between' : 'flex-col gap-0.5',
        styles.container,
      )}
    >
      <div className="flex min-w-0 flex-col gap-0.5">
        <div className="mb-0.5 flex items-center gap-1.75">
          <Icon className={iconClassName} />
          <h3 className={cn('text-[13px] font-medium', styles.title)}>{title}</h3>
        </div>
        <div className={cn('flex flex-wrap items-center gap-1.5 text-xs', styles.sub)}>
          <span>{subText}</span>
          {action && !isBlock && (
            <>
              <span className={cn('font-extralight', styles.sub)}>•</span>
              {action}
            </>
          )}
        </div>
      </div>
      {action && isBlock && <div className="flex shrink-0 text-xs">{action}</div>}
    </div>
  );
}

interface RecentRunStatusProps {
  mostRecentRun: RunWithSubsetName | null;
  allDocumentsCaseId: string | undefined;
  approvalOnly?: boolean;
}

export function RecentRunStatus({ mostRecentRun, allDocumentsCaseId, approvalOnly = false }: RecentRunStatusProps) {
  const { matterId } = useParams();
  const trackUploadBreakdown = (status: 'cancelled' | 'expired' | 'completed_with_issues' | 'completed') =>
    trackCustomEvent(TrackingEventsV2.RunViewUploadBreakdown, { matterId, runId: mostRecentRun?.id, status });
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const { hasPermission } = useHasPermission();
  const { user } = useUserContext();

  // Check if user has permission to approve runs
  const canApproveRuns = hasPermission(Permission.RunApprove) || mostRecentRun?.createdById === user?.id;

  // Blocked users can't approve (backend also enforces this)
  const blockedMessage = user?.isBlocked ? blockedReasonMessage(user.blockedReason, user.blockedUntil) : null;

  // hooks
  const { mutateAsync: submitRunApproval, isLoading: isSubmittingApproval } = useSubmitRunApproval(
    allDocumentsCaseId,
    matterId,
  );
  const handleApprove = async () => {
    if (!mostRecentRun) return;
    await submitRunApproval({ runId: mostRecentRun.id, approvalGranted: true });
    setIsPopoverOpen(false);
  };

  const handleDeny = async () => {
    if (!mostRecentRun) return;
    await submitRunApproval({ runId: mostRecentRun.id, approvalGranted: false });
    setIsPopoverOpen(false);
  };

  if (!mostRecentRun) return null;

  const isCancelled = mostRecentRun.status === RunStatus.Cancelled;
  const isApprovalTimeout = mostRecentRun.status === RunStatus.ApprovalTimeout;
  const isCompleted = !isCancelled && !isApprovalTimeout && !!mostRecentRun.completedAt;
  const isAwaitingApproval = mostRecentRun.status === RunStatus.AwaitingApproval;
  const isProcessing =
    !isCompleted && !isCancelled && !isApprovalTimeout && isProcessingStatus(mostRecentRun.status as RunStatus);

  const failedPagesCount = mostRecentRun.failedPagesCount ?? 0;

  // Alongside the processing pipeline, run progress is already clearly communicated — so the
  // only status worth surfacing separately is one that needs the user to act on approval.
  if (approvalOnly && !isAwaitingApproval) return null;

  // If the run is awaiting approval, show the approval status
  if (isAwaitingApproval) {
    const pageCount = mostRecentRun.totalPages ?? 0;
    const pageText = pageCount === 1 ? '1 page' : `${pageCount.toLocaleString()} pages`;
    const credits =
      (mostRecentRun.totalPages as number | undefined) ??
      (mostRecentRun.successfulPagesCount || 0) + (mostRecentRun.failedPagesCount || 0);
    const requiresAdminApproval = mostRecentRun.requiresAdminApproval ?? false;
    const durationText = formatDurationEstimate(mostRecentRun.durationEstimate);

    const approvalButton = (
      <Button
        size="sm"
        variant="outline"
        className="border-amber-300 bg-amber-100/80 px-2 py-1 text-xs text-amber-600 hover:border-amber-300 hover:bg-amber-100 hover:text-amber-700 disabled:cursor-not-allowed disabled:opacity-50"
        disabled={!canApproveRuns}
      >
        Approve/Cancel Run
        <Icon className="lucide--arrow-up-right" />
      </Button>
    );

    return (
      <StatusCard
        variant="amber"
        iconClassName="lucide--shield-alert text-amber-700"
        title={requiresAdminApproval ? 'Admin Approval Required' : 'Approval Required'}
        subText={
          <>
            {pageCount > 0 ? pageText : 'Pages'} pending approval ({credits.toLocaleString?.()} credits).
            {durationText && <span className="ml-1.5">Estimated {durationText}</span>}
          </>
        }
        actionPlacement="block"
        action={
          canApproveRuns ? (
            <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
              <PopoverTrigger asChild>{approvalButton}</PopoverTrigger>
              <PopoverContent align="center" sideOffset={8} className="w-96 max-w-96 rounded-lg p-0">
                <RunApprovalPopoverContent
                  credits={credits}
                  durationEstimate={mostRecentRun.durationEstimate}
                  isSubmittingApproval={isSubmittingApproval}
                  onApprove={handleApprove}
                  onDeny={handleDeny}
                  requiresAdminApproval={requiresAdminApproval}
                  canApprove={canApproveRuns}
                  blockedMessage={blockedMessage}
                />
              </PopoverContent>
            </Popover>
          ) : (
            <Tooltip>
              <TooltipTrigger>{approvalButton}</TooltipTrigger>
              <TooltipContent>You don't have permission to approve runs</TooltipContent>
            </Tooltip>
          )
        }
      />
    );
  }

  // If the run is still processing, show the processing status
  if (isProcessing) {
    const processingStatus = mostRecentRun.status as ProcessingStatus;
    const statusInfo = PipelineProcessingStageDescriptions[processingStatus];

    // Recontextualising a subset's facts doesn't reprocess pages or move through
    // the page pipeline, so the "Processing N Pages" framing doesn't apply — show
    // the run's purpose instead of a page count and stage.
    const isRecontextualisingSubset = mostRecentRun.type === RunType.UpdateSynopsis;
    if (isRecontextualisingSubset) {
      const subsetLabel = mostRecentRun.subsetName ? `"${mostRecentRun.subsetName}"` : 'this Subset';
      return (
        <StatusCard
          variant="sky"
          iconClassName="lucide--loader animate-spin text-sky-500 ease-in-out"
          title="Recontextualising Facts"
          subText={`Updating the relevance of facts in ${subsetLabel} based on its issues, and regenerating the summary.`}
        />
      );
    }

    const pageCount = mostRecentRun.totalPages ?? 0;
    const pageText = pageCount === 1 ? '1 Page' : `${pageCount.toLocaleString()} Pages`;

    return (
      <StatusCard
        variant="sky"
        iconClassName="lucide--loader animate-spin text-sky-500 ease-in-out"
        title={`Processing ${pageCount > 0 ? pageText : 'Pages'}`}
        subText={statusInfo.subTitle}
      />
    );
  }

  // If the run was cancelled
  if (isCancelled) {
    if (!mostRecentRun.completedAt) {
      return null;
    }

    const cancelledDate = new Date(mostRecentRun.completedAt);
    const oneWeekAgo = new Date();
    oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);

    if (cancelledDate < oneWeekAgo) {
      return null;
    }

    const cancelledTime = formatDate(mostRecentRun.completedAt, true, true);

    return (
      <div className="rounded-md border border-gray-200 bg-gray-50 p-3 pr-4">
        <div className="flex gap-2">
          <div className="flex gap-4">
            <div className="flex items-center gap-2">
              <Icon className="lucide--circle-x text-gray-500" />
              <h3 className="text-xs font-medium text-gray-900">Previous Run Cancelled</h3>
            </div>
            <div className="flex items-center justify-between gap-2 text-xs text-gray-600">
              <p>
                Run cancelled
                {cancelledTime && ` ${moment(cancelledDate).fromNow()} (${cancelledTime})`}
              </p>
              <span className="font-extralight text-gray-500">•</span>
              <Link
                to={`/matters/${matterId}/settings/file-history`}
                onClick={() => trackUploadBreakdown('cancelled')}
                className="flex items-center gap-1 font-medium text-blue-500 duration-150 hover:text-blue-600"
              >
                <span>View Upload Breakdown</span>
                <Icon className="lucide--arrow-right" />
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // If the run timed out waiting for approval
  if (isApprovalTimeout) {
    if (!mostRecentRun.completedAt) {
      return null;
    }

    const timedOutDate = new Date(mostRecentRun.completedAt);
    const oneWeekAgo = new Date();
    oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);

    if (timedOutDate < oneWeekAgo) {
      return null;
    }

    const timedOutTime = formatDate(mostRecentRun.completedAt, true, true);

    return (
      <div className="rounded-md border border-amber-300 bg-amber-50 p-3 pr-4">
        <div className="flex gap-2">
          <div className="flex gap-4">
            <div className="flex items-center gap-2">
              <Icon className="lucide--clock-alert text-amber-600" />
              <h3 className="text-xs font-medium text-amber-900">Previous Run Expired</h3>
            </div>
            <div className="flex items-center justify-between gap-2 text-xs text-amber-800">
              <p>
                Run expired without approval
                {timedOutTime && ` ${moment(timedOutDate).fromNow()} (${timedOutTime})`}
              </p>
              <span className="font-extralight">•</span>
              <Link
                className="flex items-center gap-1 font-medium duration-150 hover:text-amber-700"
                to={`/matters/${matterId}/settings/file-history`}
                onClick={() => trackUploadBreakdown('expired')}
              >
                <span>View Upload Breakdown</span>
                <Icon className="lucide--arrow-right" />
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // If the run is completed
  if (isCompleted) {
    const completedTime = mostRecentRun.completedAt ? formatDate(mostRecentRun.completedAt, true, true) : '';

    // Use the completed run's own counters here instead of querying live doc rows.
    // Newly completed matters can briefly have stale doc state even when the run itself succeeded.
    if (failedPagesCount > 0) {
      if (!mostRecentRun.completedAt) {
        return null;
      }

      const completedDate = new Date(mostRecentRun.completedAt);

      return (
        <StatusCard
          variant="amber"
          iconClassName="lucide--circle-alert text-amber-600"
          title="Previous Run Completed with Issues"
          subText={
            <>
              {failedPagesCount.toLocaleString()} {failedPagesCount === 1 ? 'page' : 'pages'} failed to process
              {completedTime && ` ${moment(completedDate).fromNow()} (${completedTime})`}
            </>
          }
          action={
            <Link
              className="flex items-center gap-1 font-medium duration-150 hover:text-amber-700"
              to={`/matters/${matterId}/settings/file-history`}
              onClick={() => trackUploadBreakdown('completed_with_issues')}
            >
              <span>View Upload Breakdown</span>
              <Icon className="lucide--arrow-up-right" />
            </Link>
          }
        />
      );
    }

    // Show success message if no failed pages and completed within the last 4 days
    if (!mostRecentRun.completedAt) {
      return null;
    }

    const completedDate = new Date(mostRecentRun.completedAt);
    const fourDaysAgo = new Date();
    fourDaysAgo.setDate(fourDaysAgo.getDate() - 4);

    if (completedDate < fourDaysAgo) {
      return null;
    }

    const successfulPagesCount = mostRecentRun.successfulPagesCount ?? 0;
    const pageText = successfulPagesCount === 1 ? '1 Page' : `${successfulPagesCount.toLocaleString()} Pages`;

    return (
      <StatusCard
        variant="gray"
        iconClassName="lucide--circle-check text-green-500"
        title="Recent Run Finished Processing"
        subText={
          <>
            {successfulPagesCount > 0 ? `${pageText} processed successfully` : 'All Pages processed successfully'}
            {completedTime && ` ${moment(completedDate).fromNow()} (${completedTime})`}
          </>
        }
        action={
          <Link
            to={`/matters/${matterId}/settings/file-history`}
            onClick={() => trackUploadBreakdown('completed')}
            className="flex items-center gap-1 font-medium text-blue-500 duration-150 hover:text-blue-600"
          >
            <span>View Upload Breakdown</span>
            <Icon className="lucide--arrow-up-right" />
          </Link>
        }
      />
    );
  }

  return null;
}
